// Parts shared by records, pipelines and the send queue: the form dialog, the timeline, page lines and the search box.
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../../lib'
import { Busy, Button, Copy, Empty, Err, Section, Sources, Strip, ago, since, ageSeconds, dayOf, useKeys, when } from '../kit'
import { CARD_WORD, CHANNEL_WORD, SNAPSHOT_LIMIT, SYNC_LIMIT, listBack, nameOf, neighbours, safeHref, useDebounced, withParam, type Card, type Imported, type PageRow, type Sync, type Timeline as TimelineData, type TimelineRow } from './lib'

// ---- the form dialog ----

type Vals = Record<string, string>
export type FieldDef = {
  key: string
  label: string
  kind?: 'text' | 'long' | 'select' | 'date' | 'datetime' | 'number'
  value?: string
  options?: { value: string; label: string }[] | ((v: Vals) => { value: string; label: string }[])
  required?: boolean | ((v: Vals) => boolean)
  min?: number                 // shortest text that counts
  minDate?: string
  placeholder?: string
  when?: (v: Vals) => boolean  // show this field only if
}
export type FormDef = { title: string; body?: ReactNode; fields: FieldDef[]; confirm?: string; danger?: boolean }
type Open = FormDef & { resolve: (v: Vals | null) => void }

/** A dialog of fields. It sits in .st-scrim, so page keys are off while it is open. Resolves to the values, or null. */
export function useForm() {
  const [d, setD] = useState<Open | null>(null)
  const open = (f: FormDef) => new Promise<Vals | null>((resolve) => setD({ ...f, resolve }))
  const node = d ? <FormDialog key={d.title} d={d} done={(v) => { d.resolve(v); setD(null) }} /> : null
  return { open, node }
}

function FormDialog({ d, done }: { d: Open; done: (v: Vals | null) => void }) {
  const [vals, setVals] = useState<Vals>(() => Object.fromEntries(d.fields.map((f) => [f.key, f.value ?? ''])))
  const shown = d.fields.filter((f) => !f.when || f.when(vals))
  const need = (f: FieldDef) => (typeof f.required === 'function' ? f.required(vals) : !!f.required)
  const ready = shown.every((f) => !need(f) || vals[f.key].trim().length >= (f.min ?? 1))
  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') done(null) }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  })
  const submit = (e: FormEvent) => { e.preventDefault(); if (ready) done(vals) }
  const set = (k: string, v: string) => setVals((c) => ({ ...c, [k]: v }))
  return (
    <div className="st-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) done(null) }}>
      <form className="st-dialog" role="dialog" aria-modal="true" aria-label={d.title} onSubmit={submit} data-testid="st-form">
        <h2>{d.title}</h2>
        {d.body ? <div className="st-muted">{d.body}</div> : null}
        {shown.map((f, i) => {
          const common = { value: vals[f.key], 'data-testid': `st-form-${f.key}`, autoFocus: i === 0 }
          const opts = typeof f.options === 'function' ? f.options(vals) : f.options
          return (
            <label key={f.key} className="st-field">{f.label}{need(f) ? '' : ' (optional)'}
              {f.kind === 'long' ? <textarea className="st-text" {...common} placeholder={f.placeholder} onChange={(e) => set(f.key, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && ready) done(vals) }} />
                : f.kind === 'select' ? <select className="st-select" {...common} onChange={(e) => set(f.key, e.target.value)}>{(opts ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
                  : <input className="st-input" {...common} type={f.kind === 'date' ? 'date' : f.kind === 'datetime' ? 'datetime-local' : f.kind === 'number' ? 'number' : 'text'} min={f.minDate} placeholder={f.placeholder} onChange={(e) => set(f.key, e.target.value)} />}
            </label>
          )
        })}
        <div className="st-dialog-foot">
          <Button tone="quiet" onClick={() => done(null)} data-testid="st-form-no">Cancel</Button>
          <button type="submit" className={`st-btn ${d.danger ? 'danger' : 'primary'}`} disabled={!ready} data-testid="st-form-yes">{d.confirm ?? 'Save'}</button>
        </div>
      </form>
    </div>
  )
}

// ---- small lines ----

/** The search box. The URL holds the text, a beat after typing. */
export function SearchField({ value, onChange, placeholder, testid }: { value: string; onChange: (v: string) => void; placeholder: string; testid?: string }) {
  const [text, setText] = useState(value)
  const typed = useDebounced(text, 200)
  const last = useRef(value)
  useEffect(() => { if (typed !== last.current) { last.current = typed; onChange(typed) } }, [typed, onChange])
  useEffect(() => { if (value !== last.current) { last.current = value; setText(value) } }, [value])
  return <input className="st-input st-grow" type="search" aria-label={placeholder} placeholder={placeholder} value={text} data-testid={testid} onChange={(e) => setText(e.target.value)} />
}

export function Select({ label, value, onChange, options, testid }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; testid?: string }) {
  return <select className="st-select" aria-label={label} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
}

export function Check({ label, value, onChange, testid }: { label: string; value: boolean; onChange: (v: boolean) => void; testid?: string }) {
  return <label className="st-check"><input type="checkbox" checked={value} data-testid={testid} onChange={(e) => onChange(e.target.checked)} />{label}</label>
}

/** How fresh this page's rows are: when it was read, and when the app sync last ran. With `pages`, when any page was last
 * checked. That sits here once, so no row has to repeat "Not checked". */
export function ReadLine({ at, sync, what, pages, imported, extra }: { at: string | undefined; sync: Sync | undefined; what: string; pages?: string | null; imported?: Imported; extra?: string[] }) {
  const age = ageSeconds(sync?.app)
  const syncOld = age === null || age > SYNC_LIMIT
  const main = imported?.at ? `${imported.emails_through ? `Emails through ${dayOf(imported.emails_through)}, imported` : 'Imported'} ${since(imported.at)}. Covers ${imported.covers}`
    : imported ? 'Nothing imported yet' : at ? `${what} read ${since(at)}` : 'Not read'
  const rest = [at ? `${what} read ${since(at)}` : null, sync?.app ? `App sync ${since(sync.app)}` : 'App sync not read',
    pages !== undefined ? (pages ? `Pages checked ${since(pages)}` : 'Pages not checked') : null, ...(extra ?? [])].filter(Boolean)
  return (
    <Sources>
      <span className={`st-fresh${syncOld ? ' stale' : ''}`} title={rest.join('. ')} data-testid="st-read">{main}{syncOld ? ', app sync is old' : ''}</span>
    </Sources>
  )
}

/** The filter row. On a phone everything but the search folds behind one button. */
export function FilterBar({ search, on, children, more }: { search: ReactNode; on?: number; children: ReactNode; more?: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`st-filters${open ? ' open' : ''}${more ? ' has-more' : ''}`}>
      {search}
      <button type="button" className="st-btn st-fold-btn" aria-expanded={open} data-testid="st-fold" onClick={() => setOpen((o) => !o)}>Filters{on ? ` (${on})` : ''}</button>
      <div className="st-fold">{children}</div>
      {more ? <div className="st-fold-more">{more}</div> : null}
    </div>
  )
}

/** Where a long list stands, and the way to the rest. Never a silent cap. */
export function LoadMore({ shown, total, more, loading, load }: { shown: number; total?: number; more: boolean; loading: boolean; load: () => void }) {
  if (!more) return null
  return (
    <div className="st-more" data-testid="st-more">
      <span className="st-muted">Showing {shown.toLocaleString('en-US')}{total ? ` of ${total.toLocaleString('en-US')}` : ''}</span>
      <Button onClick={load} disabled={loading} data-testid="st-load-more">{loading ? 'Loading' : 'Load more'}</Button>
    </div>
  )
}

/** j and k or the arrows walk the list this record came from, Esc goes back to it. Bound before the record loads, so a fast j j lands. */
export function useWalk(kind: string, id: string, base: string) {
  const navigate = useNavigate()
  const { prev, next } = neighbours(kind, id)
  const go = (to: string | null) => { if (to) navigate(`${base}/${to}`) }
  useKeys({
    ArrowLeft: () => go(prev), ArrowRight: () => go(next), k: () => go(prev), j: () => go(next),
    Escape: () => { const back = listBack(); if (back) navigate(back) },
  })
}

export function Walk({ kind, id, base }: { kind: string; id: string; base: string }) {
  const navigate = useNavigate()
  const { prev, next } = neighbours(kind, id)
  if (!prev && !next) return null
  return (
    <>
      <Button tone="quiet" disabled={!prev} onClick={() => navigate(`${base}/${prev}`)} data-testid="st-prev">Previous</Button>
      <Button tone="quiet" disabled={!next} onClick={() => navigate(`${base}/${next}`)} data-testid="st-next">Next</Button>
    </>
  )
}

export const TooMany = ({ shown, total }: { shown: number; total?: number }) => (
  <div className="st-banner" role="status" data-testid="st-truncated">Showing the first {shown.toLocaleString('en-US')}{total ? ` of ${total.toLocaleString('en-US')}` : ''}. Narrow it down to see the rest.</div>
)

/** A last touch: who and when, with the words on hover. A row nobody touched says nothing. */
export const lastTouch = (r: { last_at: string | null; last_actor: string | null; last_channel?: string | null; last_summary: string | null }) => (r.last_at
  ? (
    <span className="st-last" title={[when(r.last_at), r.last_summary].filter(Boolean).join(': ')}>
      <span>{nameOf(r.last_actor) || CHANNEL_WORD[r.last_channel ?? ''] || 'Someone'}, {since(r.last_at)}</span>
      {r.last_summary ? <span className="st-last-sub">{r.last_summary}</span> : null}
    </span>
  ) : null)

/** A page's last check in words. An unchecked page says nothing here: the page header says it once. */
export function pageCheck(p: Pick<PageRow, 'checked_at' | 'check_status' | 'state'>): string {
  if (!p.checked_at) return ''
  const bad = p.check_status && p.check_status >= 400 ? `, answered ${p.check_status}` : ''
  return `Checked ${since(p.checked_at)}${bad}`
}

export function PageLine({ page }: { page: PageRow }) {
  const href = safeHref(page.url)
  return (
    <div className="st-rec-line" data-testid="st-page">
      <span className="st-rec-main">{href ? <a className="st-link" href={withParam(href, 'ernd_internal', '1')} target="_blank" rel="noreferrer">{page.slug}</a> : page.slug}</span>
      {page.greeting ? <span className="st-muted" data-testid="st-page-greeting">{page.greeting}</span> : null}
      <span className="st-muted">{[page.state === 'off' ? 'Off' : null, pageCheck(page)].filter(Boolean).join(', ')}</span>
      <Copy text={page.url} label="Copy link" testid="st-page-copy" />
    </div>
  )
}

export const CardLine = ({ card }: { card: Card | null | undefined }) => card
  ? <Link className="st-link" to={`/staff/card/${card.id}`} data-testid="st-card-link">{CARD_WORD[card.status] ?? card.status}{card.submitted_at ? `, ${dayOf(card.submitted_at)}` : ''}</Link>
  : <>No request</>

// ---- the timeline ----

const KIND_LABEL: Record<string, string> = { touch: 'Touches', email: 'Emails', visit: 'Visits', app: 'App', card: 'Card', stage: 'Stages', call: 'Calls', attio: 'Attio', task: 'Tasks' }

const shortWhen = (at: string | null | undefined) => {
  const age = ageSeconds(at)
  return age !== null && age >= 0 && age < 172_800 ? `${since(at)}` : dayOf(at)
}

const SNAPSHOT_SOURCES = new Set(['Attio', 'Calendly', 'Instantly', 'Ploy'])
const stale = (at: string | null | undefined, limit: number) => { const a = ageSeconds(at); return a === null || a > limit }

function Origin({ row }: { row: TimelineRow }) {
  if (row.source && SNAPSHOT_SOURCES.has(row.source)) return <span className={`st-fresh${stale(row.read_at, SNAPSHOT_LIMIT) ? ' stale' : ''}`} title={row.read_at ? `Read ${since(row.read_at)}` : 'Not read'} data-testid="st-tl-origin">from {row.source}</span>
  if (!row.read_at) return null
  const verb = row.source?.startsWith('import') ? 'Imported' : 'Pushed'
  return <span className="st-fresh" title={when(row.read_at)}>{verb} {since(row.read_at)}</span>
}

function Row({ row, personal }: { row: TimelineRow; personal?: boolean }) {
  const email = row.kind === 'email'
  const to = row.meta?.to?.join(', ') || (row.direction === 'out' ? row.person : null)
  const from = row.meta?.from
  const sub = [
    row.said ?? (row.actor ? nameOf(row.actor) : null),
    email && row.direction === 'in' ? `from ${from ?? row.person ?? 'them'}` : null,
    email && row.direction === 'out' && to ? `to ${to}` : null,
    !email && row.person && !personal ? row.person : null,
    row.channel && row.channel !== 'note' && row.kind !== 'visit' ? CHANNEL_WORD[row.channel] ?? row.channel : null,
    row.direction === 'in' ? 'they wrote' : null,
    row.src ? `source ${row.src}` : null,
    row.owner ? `for ${nameOf(row.owner)}` : null,
    row.due_on ? `due ${dayOf(row.due_on)}` : null,
  ].filter(Boolean).join(', ')
  const attio = safeHref(row.attio_url)
  return (
    <details className="st-tl-row st-tl-open" data-kind={row.kind} data-testid="st-tl-row">
      <summary>
        <span className="st-tl-when" title={when(row.at)}>{shortWhen(row.at)}</span>
        <span className="st-tl-main">
          <span>{row.title}</span>
          {row.detail ? <span className="st-tl-sub">{row.detail}</span> : null}
          {sub ? <span className="st-tl-sub">{sub}</span> : null}
          <Origin row={row} />
          {attio ? <a className="st-link st-tl-sub" href={attio} target="_blank" rel="noreferrer">Open in Attio</a> : null}
        </span>
      </summary>
      <div className="st-tl-body" data-testid="st-tl-body">
        <p className="st-tl-sub">{when(row.at)}</p>
        {email && row.direction === 'in' ? <p className="st-tl-sub" data-testid="st-tl-from">From {from ?? row.person ?? 'them'}</p> : null}
        {email && row.direction === 'out' && to ? <p className="st-tl-sub" data-testid="st-tl-to">To {to}</p> : null}
        {row.meta?.cc?.length ? <p className="st-tl-sub">cc {row.meta.cc.join(', ')}</p> : null}
        {row.body ? <>{row.meta?.body_source ? <p className="st-tl-sub">{row.meta.body_source === 'as drafted' ? 'As drafted' : row.meta.body_source}</p> : null}<pre className="st-send-msg">{row.body}</pre></> : null}
        {row.body_note ? <p className="st-note" data-testid="st-tl-note">{row.body_note}</p> : null}
      </div>
    </details>
  )
}

/** Everything that happened, newest first. `path` is the record's API path. */
export function TimelineView({ path, right, personal, emptyWords }: { path: string; right?: ReactNode; personal?: boolean; emptyWords?: string }) {
  const [kind, setKind] = useState('all')
  const q = useQuery({
    queryKey: ['crm', 'timeline', path, kind],
    queryFn: () => api<TimelineData>('GET', `${path}/timeline${kind === 'all' ? '' : `?kind=${kind}`}`),
    placeholderData: (p) => p,
  })
  const kinds = q.data?.kinds ?? {}
  const total = Object.values(kinds).reduce((a, b) => a + b, 0)
  const items = [{ key: 'all', label: 'All', n: total }, ...Object.keys(KIND_LABEL).filter((k) => kinds[k]).map((k) => ({ key: k, label: KIND_LABEL[k], n: kinds[k] }))]
  return (
    <Section title="Timeline" right={right} testid="st-timeline">
      {q.error ? <Err error={q.error} /> : !q.data ? <Busy /> : (
        <>
          <Strip items={items} value={kind} onChange={setKind} testid="st-tl-kind" />
          {q.data.rows.length ? <div className="st-tl">{q.data.rows.map((r, i) => <Row key={`${r.kind}:${r.id}:${i}`} row={r} personal={personal} />)}</div> : <Empty>{emptyWords ?? 'No touch on record.'}</Empty>}
          <p className="st-note" data-testid="st-tl-covers">History covers {q.data.covers}</p>
          {q.data.truncated ? <p className="st-note">Showing the newest {q.data.rows.length}. Older rows are cut.</p> : null}
        </>
      )}
    </Section>
  )
}
